import React from 'react';
import { useTranslation } from 'react-i18next';
import { useShellStore } from '../../../shell/store/shellStore';
import { useEsaDomainStore } from '../store/esaDomainStore';
import { useEsaConverse } from '../hooks/useEsaConverse';
import { useEsaBusSendMessage } from '../hooks/useEsaBusSendMessage';
import { useReturningUser, clearNewConversationSession } from '../hooks/useReturningUser';
import { useEsaResolveOnOpenBus } from '../hooks/useEsaResolveOnOpenBus';
import { useEsaResolveHilEvent } from '../hooks/useEsaResolveHilEvent';
import { deleteConversation, updateConversation } from '../services/esaApi';
import { getMessageCache } from '../cache/messageCacheInstance';
import {
  buildTranscriptFilename,
  downloadTextFile,
  formatConversationTranscript,
} from '../utils/formatConversationTranscript';
import { getSolutionRunStore } from '../../Canvas/store/solutionRunStore';
import { ESATopBar } from './panel/ESATopBar';
import { ESAPanelRouter } from './panel/ESAPanelRouter';
import { ESAComposer } from './composer/ESAComposer';
import { AttentionWidget } from './briefing/AttentionWidget';
import { useSolutionRunActions } from '../hooks/useSolutionRunActions';
import { useThreadMessages } from '../hooks/useThreadMessages';
import { useFileUpload } from '../hooks/useFileUpload';
import type { ConverseAttachment } from '../constants/fileUploadConstants';

interface SuggestionChipProps {
  label: string;
  onClick: () => void;
  variant?: 'outline' | 'filled';
  disabled?: boolean;
}

function SuggestionChip({ label, onClick, variant = 'outline', disabled = false }: SuggestionChipProps): React.ReactElement {
  const base = 'inline-flex items-center h-[25px] px-[14px] rounded-full text-overline font-medium whitespace-nowrap shrink-0 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed esa-chip-transition';
  // esa-chip-* classes are defined in index.css OUTSIDE @layer utilities so they survive
  // canvas stylesheet injection (Module Federation style-loader appends after host CSS).
  const styles = variant === 'filled'
    ? 'esa-chip-filled bg-esa-accent text-brand-on-primary border border-esa-accent'
    : 'esa-chip-idle border border-esa-accent text-esa-accent bg-surface';
  return (
    <button type="button" onClick={onClick} disabled={disabled} className={`${base} ${styles}`}>
      {label}
    </button>
  );
}

/**
 * Horizontally scrollable, drag-to-scroll strip for the idle suggestion chips.
 * Mirrors the single-row design: no wrap, hidden scrollbar, grab cursor. A drag
 * that moves the pointer is suppressed at click-capture so it never fires a chip.
 */
function SuggestionChipStrip({ children }: { children: React.ReactNode }): React.ReactElement {
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const drag = React.useRef({ down: false, moved: false, startX: 0, scrollLeft: 0 });
  const [fadeLeft, setFadeLeft] = React.useState(false);
  const [fadeRight, setFadeRight] = React.useState(false);

  const updateEdgeFades = React.useCallback((): void => {
    const el = scrollRef.current;
    if (!el) return;
    setFadeLeft(el.scrollLeft > 2);
    setFadeRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
  }, []);

  React.useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateEdgeFades();
    const observer = new ResizeObserver(updateEdgeFades);
    observer.observe(el);
    el.addEventListener('scroll', updateEdgeFades, { passive: true });
    return () => {
      observer.disconnect();
      el.removeEventListener('scroll', updateEdgeFades);
    };
  }, [updateEdgeFades, children]);

  const handlePointerDown = React.useCallback((e: React.PointerEvent<HTMLDivElement>): void => {
    const el = scrollRef.current;
    if (!el) return;
    // Don't capture the pointer here — capturing on pointerdown re-targets the
    // subsequent click to this container so chip buttons never fire. We only
    // capture once a real drag is detected (see handlePointerMove).
    drag.current = { down: true, moved: false, startX: e.clientX, scrollLeft: el.scrollLeft };
  }, []);

  const handlePointerMove = React.useCallback((e: React.PointerEvent<HTMLDivElement>): void => {
    const el = scrollRef.current;
    if (!el || !drag.current.down) return;
    const dx = e.clientX - drag.current.startX;
    if (!drag.current.moved && Math.abs(dx) > 4) {
      drag.current.moved = true;
      el.setPointerCapture(e.pointerId);
    }
    if (drag.current.moved) {
      el.scrollLeft = drag.current.scrollLeft - dx;
      updateEdgeFades();
    }
  }, [updateEdgeFades]);

  const handlePointerUp = React.useCallback((e: React.PointerEvent<HTMLDivElement>): void => {
    drag.current.down = false;
    const el = scrollRef.current;
    if (el?.hasPointerCapture(e.pointerId)) el.releasePointerCapture(e.pointerId);
    updateEdgeFades();
  }, [updateEdgeFades]);

  const handleClickCapture = React.useCallback((e: React.MouseEvent<HTMLDivElement>): void => {
    if (drag.current.moved) {
      e.preventDefault();
      e.stopPropagation();
    }
  }, []);

  const fadeClass = [
    fadeLeft ? 'esa-suggestion-strip--fade-left' : '',
    fadeRight ? 'esa-suggestion-strip--fade-right' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className={`esa-suggestion-strip relative w-full ${fadeClass}`}>
      <div
        ref={scrollRef}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerUp}
        onClickCapture={handleClickCapture}
        className="flex flex-nowrap items-center gap-[6px] w-full overflow-x-auto cursor-grab active:cursor-grabbing select-none hide-scrollbar"
      >
        {children}
      </div>
    </div>
  );
}

/**
 * Root ESA Panel component — host-owned, always present in Layout.tsx.
 * ESAComposer is lifted here so it is visible in both `briefing` and `chat` modes.
 */
export function EsaPanel(): React.ReactElement {
  const { t } = useTranslation();
  const { sendMessage } = useEsaConverse();
  useEsaBusSendMessage(sendMessage);
  useEsaResolveOnOpenBus();
  useEsaResolveHilEvent();
  useReturningUser();
  const { status: runStatus, isInitiating, isReviewing, handleReview, handleInitiate, handleKeepEditing, handleCancel } = useSolutionRunActions(sendMessage);

  // Track which conversation was bootstrapped for the current run so chips are
  // only shown when the user is looking at that thread.
  const [runConvId, setRunConvId] = React.useState<string | null>(
    () => getSolutionRunStore().getState().conversationId,
  );
  React.useEffect(
    () => getSolutionRunStore().subscribe(s => setRunConvId(s.conversationId)),
    [],
  );

  const esaPanelOpen = useShellStore(s => s.esaPanelOpen);
  const setEsaPanelOpen = useShellStore(s => s.setEsaPanelOpen);
  const panelMode = useShellStore(s => s.esaPanelMode);
  const setEsaPanelMode = useShellStore(s => s.setEsaPanelMode);

  const activeThreadId = useEsaDomainStore(s => s.activeThreadId);
  const threadIndex = useEsaDomainStore(s => s.threadIndex);
  const pendingHITL = useEsaDomainStore(s => s.pendingHITL);
  const composerDraft = useEsaDomainStore(s => s.composerDraft);
  const setComposerDraft = useEsaDomainStore(s => s.setComposerDraft);
  const streamStatus = useEsaDomainStore(s => s.streamStatus);
  const upsertThread = useEsaDomainStore(s => s.upsertThread);
  const removeThread = useEsaDomainStore(s => s.removeThread);
  const setActiveThread = useEsaDomainStore(s => s.setActiveThread);
  const hasUnreadNotification = useEsaDomainStore(s => s.hasUnreadNotification);
  const setHasUnreadNotification = useEsaDomainStore(s => s.setHasUnreadNotification);
  const activeThread = activeThreadId ? threadIndex.get(activeThreadId) : undefined;
  const threadTitle = activeThread?.title ?? null;
  const hitlCount = pendingHITL.length;
  const isStreaming = streamStatus === 'connecting' || streamStatus === 'streaming';

  const { isAttachEnabled } = useFileUpload();

  const handleSend = React.useCallback(async (text: string, _attachments?: ConverseAttachment[]): Promise<void> => {
    if (panelMode === 'briefing') setEsaPanelMode('chat');
    await sendMessage(text);
  }, [sendMessage, panelMode, setEsaPanelMode]);

  const handleClose = (): void => {
    setEsaPanelOpen(false);
  };

  const handleInbox = (): void => {
    setHasUnreadNotification(false);
    setEsaPanelMode('inbox');
    if (!esaPanelOpen) setEsaPanelOpen(true);
  };

  const handleRenameCommit = React.useCallback((newTitle: string): void => {
    if (!activeThreadId || !activeThread) return;
    // Optimistic update — reflects immediately in the header and thread list.
    upsertThread({ ...activeThread, title: newTitle });
    // Persist to backend; fire-and-forget (failures are non-blocking for UX).
    updateConversation(activeThreadId, { title: newTitle }).catch(() => {});
  }, [activeThreadId, activeThread, upsertThread]);

  const activeMessages = useThreadMessages(activeThreadId, getMessageCache());
  const canDownloadTranscript = activeMessages.some(
    (m) => !m.id.startsWith('__streaming_'),
  );

  const handleDownloadTranscript = React.useCallback((): void => {
    if (!activeThreadId) return;
    const messages = getMessageCache().getSnapshot(activeThreadId);
    const content = formatConversationTranscript({
      title: threadTitle,
      conversationId: activeThreadId,
      messages,
    });
    downloadTextFile(buildTranscriptFilename(threadTitle), content);
  }, [activeThreadId, threadTitle]);

  const handleDeleteConversation = React.useCallback((): void => {
    if (!activeThreadId) return;
    const label = threadTitle?.trim() || t('esa.newConversation', 'New conversation');
    const confirmed = window.confirm(
      t('esa.deleteConversationConfirm', 'Delete "{{title}}"? This cannot be undone.', {
        title: label,
      }),
    );
    if (!confirmed) return;

    const deletedId = activeThreadId;
    removeThread(deletedId);
    getMessageCache().evict(deletedId);
    clearNewConversationSession();
    setActiveThread(null);
    setEsaPanelMode('briefing');
    deleteConversation(deletedId).catch(() => {});
  }, [activeThreadId, threadTitle, t, removeThread, setActiveThread, setEsaPanelMode]);

  const handleAttentionClick = (): void => {
    setEsaPanelMode('inbox');
    if (!esaPanelOpen) setEsaPanelOpen(true);
  };

  if (!esaPanelOpen) {
    return (
      <div className="fixed bottom-5 right-5 z-[1000]">
        <AttentionWidget
          hitlCount={hitlCount}
          activeTaskCount={0}
          onClick={handleAttentionClick}
        />
      </div>
    );
  }

  /* Composer is shown in briefing + chat modes but hidden in thread-list */
  const showComposer = panelMode === 'briefing' || panelMode === 'chat';

  const suggestions = [
    t('esa.suggestionAttention', 'What needs my attention?'),
    t('esa.suggestionContext', 'What am I looking at?'),
    t('esa.suggestionShowAllRuns', 'Show all runs'),
    t('esa.suggestionStartNewRun', 'Start a new run'),
    t('esa.suggestionQualityIssues', 'Show quality issues'),
    t('esa.suggestionWhatChanged', 'What changed since last run?'),
    t('esa.suggestionBlockedRecords', 'Show blocked records'),
    t('esa.suggestionSummarizeRuns', 'Summarize recent runs'),
    t('esa.suggestionImproveQuality', 'How do I improve quality scores?'),
    t('esa.suggestionCapabilities', 'What all can I do?'),
  ];

  return (
    <div
      data-testid="esa-panel"
      className="flex flex-col w-full h-full overflow-hidden relative bg-esa-panel"
    >
      {/* Top bar hidden in thread-list and resolve — both modes have their own dedicated header */}
      {panelMode !== 'thread-list' && panelMode !== 'resolve' && (
        <ESATopBar
          threadTitle={threadTitle}
          panelMode={panelMode}
          hitlCount={hitlCount}
          savedStatus={activeThread?.status === 'saved'}
          hasUnreadNotification={hasUnreadNotification}
          onClose={handleClose}
          onInbox={handleInbox}
          onNewConversation={() => { clearNewConversationSession(); setActiveThread(null); setEsaPanelMode('briefing'); }}
          onSwitchConversation={() => setEsaPanelMode('thread-list')}
          onRenameCommit={activeThreadId ? handleRenameCommit : undefined}
          onDownloadTranscript={activeThreadId ? handleDownloadTranscript : undefined}
          downloadDisabled={!canDownloadTranscript}
          onDeleteConversation={activeThreadId ? handleDeleteConversation : undefined}
        />
      )}

      {/* Main content area — takes remaining height */}
      <div className="flex-1 overflow-hidden flex flex-col min-h-0">
        <ESAPanelRouter />
      </div>

      {/* Suggestion chips — context-aware based on solution run lifecycle.
          Run-specific chips are only shown when the user is on the conversation
          that was bootstrapped for this run. Switching threads hides them; coming
          back to the run thread restores them. */}
      {showComposer && (
        <div className="px-3 pt-0 pb-1.5 shrink-0">
          {runStatus === 'drafting' && runConvId !== null && runConvId === activeThreadId && (
            <div className="flex flex-row flex-wrap gap-2">
              <SuggestionChip
                label={isReviewing ? 'Sending…' : 'Review & initiate'}
                variant="filled"
                disabled={isReviewing}
                onClick={() => void handleReview()}
              />
              <SuggestionChip
                label="Cancel"
                variant="outline"
                disabled={isReviewing}
                onClick={handleCancel}
              />
            </div>
          )}
          {(runStatus === 'reviewing' || runStatus === 'error') && runConvId !== null && runConvId === activeThreadId && (
            <div className="flex flex-row flex-wrap gap-2">
              <SuggestionChip
                label={isInitiating ? 'Initiating…' : 'Initiate Run'}
                variant="filled"
                disabled={isInitiating}
                onClick={() => void handleInitiate()}
              />
              <SuggestionChip
                label="Keep Editing"
                variant="outline"
                disabled={isInitiating}
                onClick={handleKeepEditing}
              />
              <SuggestionChip
                label="Cancel"
                variant="outline"
                disabled={isInitiating}
                onClick={handleCancel}
              />
            </div>
          )}
          {runStatus === 'idle' && (
            <SuggestionChipStrip>
              {suggestions.map((text, i) => (
                <SuggestionChip
                  key={i}
                  label={text}
                  onClick={() => void handleSend(text)}
                />
              ))}
            </SuggestionChipStrip>
          )}
        </div>
      )}
      {/* Shared composer — persists between briefing and chat modes */}
      {showComposer && (
        <ESAComposer
          draft={composerDraft}
          onDraftChange={setComposerDraft}
          onSend={handleSend}
          isAttachEnabled={isAttachEnabled}
          isStreaming={isStreaming}
        />
      )}

    </div>
  );
}
